import { Button, Form, Input, Modal, Select } from 'antd'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { connect } from 'react-redux'

// Replacement for the retired FreshDesk widget (the classic
// assets.freshdesk.com script is on common ad/privacy blocklists and had
// quietly stopped rendering for users). A right-edge "Help" tab opens a small
// form; submitting POSTs to /api/notifications/support, which emails BAM
// support via SES with the message plus diagnostic context — no local mail
// client required. Direct mailto remains only as the failure fallback.
//
// A category dropdown routes the request: "Technical/App Support" goes to
// BAM support; "Product/Sales Support" goes to the address the tenant's admin
// set in DAM Settings > General (BAM support bcc'd), falling back to BAM
// support when none is set. The backend resolves that address from the
// hostname we send — the client never names a recipient.

const SUPPORT_EMAIL = 'support@bam.zone'
const HELP_CENTER_URL = 'https://help.bam.zone/admin'
// Most specific route first. Hash links target sections of the admin guide.
const HELP_ARTICLES = [
  ['/settings/permissions/invitations', 'articles/invite-users.html'],
  ['/settings/permissions/users', 'articles/invite-users.html'],
  ['/settings/permissions/approval', 'articles/invite-users.html#approving'],
  ['/settings/permissions/groups', 'articles/setting-up-groups.html'],
  ['/settings/permissions/roles', 'articles/permissions.html'],
  ['/settings/mst/branding', 'articles/customize-branding.html'],
  ['/settings/mst/featured', 'articles/customize-featured.html'],
  ['/settings/mst/menu', 'articles/customize-menu.html'],
  ['/settings/mst/resources', 'articles/customize-resources.html'],
  ['/settings/dam/general', 'articles/share-link-settings.html'],
  ['/settings/dam/builders', 'articles/comparison-disclaimers.html'],
  ['/settings/dam/profiles', 'articles/customer-profiles-settings.html'],
  ['/analytics', 'articles/usage-analytics.html'],
  ['/builders', 'articles/custom-assets-overview.html'],
  ['/library/_ugc', 'articles/review-uploads.html'],
  ['/library', 'admin#content'],
  ['/asset', 'admin#content'],
  ['/profile', 'articles/edit-profile.html'],
]
const MAX_MESSAGE_LENGTH = 1500
const CATEGORY_TECHNICAL = 'technical'
const CATEGORY_SALES = 'sales'
const CATEGORY_OPTIONS = [
  { value: CATEGORY_TECHNICAL, label: 'Technical/App Support' },
  { value: CATEGORY_SALES, label: 'Product/Sales Support' },
]

const helpLink = () => {
  const { pathname } = window.location
  const match = HELP_ARTICLES.find(
    ([route]) => pathname === route || pathname.startsWith(`${route}/`),
  )
  return match
    ? { href: `https://help.bam.zone/${match[1]}`, label: 'Help for this page' }
    : { href: HELP_CENTER_URL, label: 'Knowledge Base' }
}

const collectDiagnostics = () => {
  const diagnostics = {
    domain: window.location.hostname,
    page: window.location.pathname,
    userAgent: navigator.userAgent,
    time: new Date().toISOString(),
  }
  if (navigator.platform) diagnostics.platform = navigator.platform
  if (navigator.language) diagnostics.language = navigator.language
  if (window.screen && window.screen.width) {
    diagnostics.screen = `${window.screen.width}x${window.screen.height}`
  }
  try {
    diagnostics.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (err) {
    // Intl always exists in supported browsers; ignore if not
  }
  return diagnostics
}

const submitSupportRequest = (values, user, config) => {
  const loggedIn = !!(user && user.loggedIn)
  const payload = {
    message: values.message,
    website: values.website || undefined,
    email: loggedIn ? user.email : values.email,
    name: (loggedIn && user.name) || undefined,
    // profile fields, only when set on the logged-in user
    phone: (loggedIn && user.phone) || undefined,
    company: (loggedIn && user.company) || undefined,
    loggedIn,
    category: values.category || CATEGORY_TECHNICAL,
    // tenant hint for sales routing; resolved server-side
    tenant: window.location.hostname,
    diagnostics: collectDiagnostics(),
  }
  const baseUrl = (config && config.baseUrl) || ''
  return fetch(`${baseUrl}/api/notifications/support`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then((res) => {
    if (!res.ok) throw new Error(`support request failed (${res.status})`)
    return res.json()
  })
}

const SupportWidget = ({ user, config, salesSupportEmail }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [verifyEmail, setVerifyEmail] = useState(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState(null)
  const help = helpLink()
  const [form] = Form.useForm()

  const loggedIn = user && user.loggedIn
  // Sales requests reach the tenant's own team; say so when we know the
  // address is configured (settings only load once logged in)
  const salesConfigured = !!(loggedIn && salesSupportEmail)

  const handleSubmit = (values) => {
    setSending(true)
    setSendError(null)
    submitSupportRequest(values, user, config)
      .then((result) => {
        if (result && result.verificationRequired) {
          // unknown logged-out email: the backend held the request and
          // emailed a confirmation link instead
          setVerifyEmail(values.email)
        } else {
          setSubmitted(true)
        }
      })
      .catch((err) => {
        console.error(err)
        setSendError(
          'Your request could not be submitted. Please try again, or email ',
        )
      })
      .then(() => {
        setSending(false)
      })
  }

  const handleClose = () => {
    setIsOpen(false)
    setSubmitted(false)
    setVerifyEmail(null)
    setSendError(null)
    form.resetFields()
  }

  return (
    <React.Fragment>
      <button
        type="button"
        style={styles.helpTab}
        className="support-widget__tab"
        onClick={() => setIsOpen(true)}
      >
        Help
      </button>
      <Modal
        title="BAM! Help and Support"
        open={isOpen}
        onCancel={handleClose}
        footer={null}
        destroyOnClose
      >
        {submitted || verifyEmail ? (
          <div>
            {verifyEmail ? (
              <p>
                Almost done — we&apos;ve sent a confirmation link to{' '}
                <b>{verifyEmail}</b>. Open it to deliver your message to
                support.
              </p>
            ) : (
              <p>
                Your request has been sent. Expect a response within one
                business day.
              </p>
            )}
            <div style={styles.footerRow}>
              <a href={help.href} target="_blank" rel="noopener noreferrer">
                {help.label}
              </a>
              <Button type="primary" onClick={handleClose}>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ category: CATEGORY_TECHNICAL }}
          >
            {/* Honeypot: hidden from humans; the backend silently drops any
                submission that fills it */}
            <Form.Item name="website" style={styles.honeypot}>
              <Input tabIndex={-1} autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="category"
              label="What do you need help with?"
              rules={[{ required: true, message: 'Choose a support type' }]}
            >
              <Select options={CATEGORY_OPTIONS} />
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {() => (
                <p style={styles.categoryNote}>
                  {form.getFieldValue('category') === CATEGORY_SALES
                    ? salesConfigured
                      ? 'Product and sales questions go directly to your ' +
                        "organization's sales support team."
                      : 'Product and sales questions are forwarded to your ' +
                        "organization's sales support team."
                    : 'Technical and app questions go to the app support team.'}
                </p>
              )}
            </Form.Item>
            {!loggedIn && (
              <Form.Item
                name="email"
                label="Your email"
                rules={[
                  {
                    required: true,
                    type: 'email',
                    message: 'Enter an email address we can reply to',
                  },
                ]}
              >
                <Input placeholder="you@company.com" />
              </Form.Item>
            )}
            <Form.Item
              name="message"
              label="How can we help?"
              rules={[
                { required: true, message: 'Describe your question or issue' },
              ]}
            >
              <Input.TextArea
                rows={6}
                maxLength={MAX_MESSAGE_LENGTH}
                showCount
                placeholder="Describe your question or issue"
              />
            </Form.Item>
            {sendError && (
              <p style={styles.errorNote}>
                {sendError}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              </p>
            )}
            <Form.Item style={styles.submitRow}>
              <div style={styles.footerRow}>
                <a href={help.href} target="_blank" rel="noopener noreferrer">
                  {help.label}
                </a>
                <Button type="primary" htmlType="submit" loading={sending}>
                  Submit
                </Button>
              </div>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </React.Fragment>
  )
}

const styles = {
  // Right-edge tab, matching the old FreshDesk placement (right side,
  // vertical, dark). Kept as a plain element + inline styles so brand
  // button rules don't restyle it.
  helpTab: {
    position: 'fixed',
    top: '175px',
    right: 0,
    zIndex: 1000,
    transform: 'rotate(-90deg)',
    transformOrigin: 'bottom right',
    backgroundColor: '#242424',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px 4px 0 0',
    padding: '6px 18px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  honeypot: {
    display: 'none',
  },
  errorNote: {
    color: '#cf1322',
    fontSize: '13px',
  },
  categoryNote: {
    color: '#666',
    fontSize: '13px',
    marginTop: '-12px',
    marginBottom: '16px',
  },
  submitRow: {
    marginBottom: 0,
  },
  // "Knowledge Base" link bottom-left, action button bottom-right
  footerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}

SupportWidget.propTypes = {
  user: PropTypes.object,
  config: PropTypes.object,
  salesSupportEmail: PropTypes.string,
}

const mapStateToProps = (state) => ({
  user: state.user,
  config: state.config.appConfig,
  salesSupportEmail:
    state.settings && state.settings.general
      ? state.settings.general.salesSupportEmail
      : undefined,
})

export default connect(mapStateToProps)(SupportWidget)
